import { purchasedGoodsRoutes } from '@app/purchased-goods/purchased-goods-routes';
import Text from '@shared/components/content/text';
import { Link } from 'react-router-dom';
import { ScopeCategorySlug } from '../../slugs';

type AdditionalCategoryDescriptions = Record<ScopeCategorySlug, JSX.Element>;

const additionalCategoryDescriptions: Partial<AdditionalCategoryDescriptions> =
  {
    'other-purchased-goods': (
      <>
        <Text>
          Other purchased goods and services, as part of Scope 3, Category 1 -
          Purchased Goods and Services, covers the cradle-to-gate emissions from
          everyday goods and services you buy to run your business, excluding
          your food and packaging purchases.
        </Text>
        <Text>
          Your{' '}
          <Link
            to={purchasedGoodsRoutes.PURCHASED_FOOD_AND_DRINK}
            className="font-medium text-teal-600"
          >
            food and drink
          </Link>{' '}
          and{' '}
          <Link
            to={purchasedGoodsRoutes.PURCHASED_PACKAGING}
            className="font-medium text-teal-600"
          >
            packaging
          </Link>{' '}
          purchases are managed via the purchased goods area of our platform,
          where our AI-powered tool will allow you to easily import your
          purchase data from a spreadsheet.
        </Text>
        <Text className="mt-3">
          Examples of other purchased goods or services include:
        </Text>
        <Text>
          <ul className="list-disc pl-5">
            <li>Purchased water</li>
            <li>Stationery</li>
            <li>Digital services</li>
            <li>Marketing services</li>
          </ul>
        </Text>
      </>
    ),
    'capital-goods': (
      <>
        <Text>
          Scope 3, Category 2 - Capital Goods refers to the emissions created
          when long-term assets you purchase are manufactured and delivered.
          These are assets that you use over many years.
        </Text>
        <Text className="mt-3">Examples include:</Text>
        <Text>
          <ul className="list-disc pl-5">
            <li>Building a new food production facility</li>
            <li>Manufacturing equipment</li>
            <li>Company vehicles</li>
          </ul>
        </Text>
      </>
    ),
    waste: (
      <>
        <Text>
          Scope 3, Category 5 - Waste Generated in Operations includes the
          emissions created when waste from your operations is treated,
          recycled, or disposed of by external waste contractors.
        </Text>
        <Text className="mt-3">Examples include:</Text>
        <Text>
          <ul className="list-disc pl-5">
            <li>Landfilling general waste</li>
            <li>Recycling cardboard and plastics</li>
            <li>Treating wastewater</li>
          </ul>
        </Text>
      </>
    ),
    'business-travel': (
      <>
        <Text>
          Scope 3, Category 6 - Business Travel refers to emissions created when
          your employees travel for work using transport not owned or operated
          by your company.
        </Text>
        <Text className="mt-3">Examples include:</Text>
        <Text>
          <ul className="list-disc pl-5">
            <li>Flights to site visits</li>
            <li>Train travel for conferences</li>
            <li>
              Stays at hotels during business trips (<i>optional inclusion</i>)
            </li>
          </ul>
        </Text>
      </>
    ),
    'employee-commuting': (
      <>
        <Text>
          Scope 3, Category 7 - Employee Commuting includes the emissions
          created when your employees travel between their homes and your
          workplaces.
        </Text>
        <Text className="mt-3">Examples include:</Text>
        <Text>
          <ul className="list-disc pl-5">
            <li>Driving to and from work in employee vehicles</li>
            <li>Public transport commuting</li>
            <li>
              Remote work (<i>optional inclusion</i>)
            </li>
          </ul>
        </Text>
      </>
    ),
    'leased-assets-upstream': (
      <>
        <Text>
          Scope 3, Category 8 - Upstream Leased Assets refers to emissions from
          assets you lease and operate that are not already included in your
          Scope 1 or 2.
        </Text>
        <Text className="mt-3">Examples include:</Text>
        <Text>
          <ul className="list-disc pl-5">
            <li>Leasing office buildings</li>
            <li>Renting warehouse space</li>
            <li>Leasing processing equipment</li>
          </ul>
        </Text>
      </>
    ),
    'sold-products-processing': (
      <>
        <Text>
          Scope 3, Category 10 - Processing of Sold Products refers to emissions
          created when your customers further process your{' '}
          <span className="font-semibold">intermediate</span> products into
          finished consumer products.
        </Text>
        <Text className="mt-3">Examples include:</Text>
        <Text>
          <ul className="list-disc pl-5">
            <li>Sugar processed into candy</li>
            <li>Flour processed into bread and pasta</li>
            <li>Cocoa processed into chocolate products</li>
          </ul>
        </Text>
      </>
    ),
    'sold-products-use': (
      <>
        <Text>
          Scope 3, Category 11 - Use of Sold Products includes emissions created
          when consumers use your food and beverage products, especially where
          energy is required during preparation.
        </Text>
        <Text className="mt-3">Examples include:</Text>
        <Text>
          <ul className="list-disc pl-5">
            <li>Cooking frozen meals in ovens</li>
            <li>Brewing coffee using electricity</li>
            <li>Refrigerating beverages at home</li>
          </ul>
        </Text>
      </>
    ),
    'sold-products-end-of-life': (
      <>
        <Text>
          Scope 3, Category 12 - End-of-Life Treatment of Sold Products includes
          emissions created when your food and beverage products and their
          packaging are disposed of after use.
        </Text>
        <Text className="mt-3">Examples include:</Text>
        <Text>
          <ul className="list-disc pl-5">
            <li>Landfilling/composting leftover food</li>
            <li>Recycling drinks containers</li>
            <li>Incinerating food packaging</li>
          </ul>
        </Text>
      </>
    ),
    'leased-assets-downstream': (
      <>
        <Text>
          Scope 3, Category 13 - Downstream Leased Assets includes emissions
          from assets you own but lease out for others to operate, where not
          included in Scope 1 or 2.
        </Text>
        <Text className="mt-3">Examples include:</Text>
        <Text>
          <ul className="list-disc pl-5">
            <li>Leasing refrigerators</li>
            <li>Leasing production line machinery</li>
            <li>Leasing beverage dispensing equipment</li>
          </ul>
        </Text>
      </>
    ),
    franchises: (
      <>
        <Text>
          Scope 3, Category 14 - Franchises covers emissions from branded food
          and beverage outlets that operate under your name but are not owned by
          you, which are not already included in Scope 1 or 2.
        </Text>
        <Text className="mt-3">Examples include:</Text>
        <Text>
          <ul className="list-disc pl-5">
            <li>Franchise fast food restaurants</li>
            <li>Franchise cafés and coffee shops</li>
            <li>Franchise convenience food outlets</li>
          </ul>
        </Text>
      </>
    ),
    investments: (
      <>
        <Text>
          Scope 3, Category 15 - Investments includes the emissions created by
          the companies, projects, or assets you financially support like
          through shares or loans but do not directly operate.
        </Text>
        <Text className="mt-3">Examples include:</Text>
        <Text>
          <ul className="list-disc pl-5">
            <li>Investments in farms</li>
            <li>Loans to food manufacturing companies</li>
            <li>Holdings in businesses</li>
          </ul>
        </Text>
      </>
    ),
  };

export default additionalCategoryDescriptions;
